<script setup>
import { create_intervention_progress, get_media_url } from '@/services/intervention';
import emptyCard from '@/components/emptyCard.vue';
import { ref, computed } from 'vue';
import card_header from '@/components/card_header.vue';
import disabledCard from '@/components/disabledCard.vue';
import { formattedDate, swalError, swalSuccess } from '@/helper';
import { useStore } from 'vuex';

const store = useStore();

// Modal state
const showProofModal = ref(false);
const proofMedia = ref({
  url: null,
  filename: null,
  contentType: null,
  isLoading: false
});

const props = defineProps({
  age_group_term:{
    type: Object
  },
  trait_config:{
    type: Object
  },
  intervention:{
    type: Object
  },
  activities:{
    type: Array
  },
  child:{
    type: Object
  }
})

// Upload state management
const uploadState = ref({});
const initActivityUploadState = (activityId) => {
  if (!uploadState.value[activityId]) {
    uploadState.value[activityId] = {
      showUpload: false,
      file: null,
      preview: null,
      uploadProgress: 0,
      isUploading: false,
      isDragging: false,
      fileType: null // 'image' or 'video'
    };
  }
};

// File validation
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    swalError('File size must be less than 50MB');
    return false;
  }
  
  const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
  const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);
  
  if (!isImage && !isVideo) {
    swalError('Please upload an image (JPEG, PNG, GIF, WebP) or video (MP4, MOV, AVI, WebM)');
    return false;
  }
  
  return { isValid: true, fileType: isImage ? 'image' : 'video' };
};

// Toggle upload panel
const toggleUpload = (activityId) => {
  initActivityUploadState(activityId);
  const state = uploadState.value[activityId];
  
  if (state.showUpload) {
    // Close and reset
    state.showUpload = false;
    state.file = null;
    state.preview = null;
    state.uploadProgress = 0;
  } else {
    // Open
    state.showUpload = true;
  }
};

// Handle file selection
const handleFileSelect = (event, activityId) => {
  const file = event.target.files[0];
  if (file) {
    processFile(file, activityId);
  }
};

// Handle drag and drop
const handleDragOver = (event, activityId) => {
  event.preventDefault();
  initActivityUploadState(activityId);
  uploadState.value[activityId].isDragging = true;
};

const handleDragLeave = (event, activityId) => {
  event.preventDefault();
  if (uploadState.value[activityId]) {
    uploadState.value[activityId].isDragging = false;
  }
};

const handleDrop = (event, activityId) => {
  event.preventDefault();
  initActivityUploadState(activityId);
  uploadState.value[activityId].isDragging = false;
  
  const file = event.dataTransfer.files[0];
  if (file) {
    processFile(file, activityId);
  }
};

// Process and preview file
const processFile = (file, activityId) => {
  const validation = validateFile(file);
  if (!validation) return;
  
  const state = uploadState.value[activityId];
  state.file = file;
  state.fileType = validation.fileType;
  
  // Create preview
  const reader = new FileReader();
  reader.onload = (e) => {
    state.preview = e.target.result;
  };
  reader.readAsDataURL(file);
};

// Remove selected file
const removeFile = (activityId) => {
  const state = uploadState.value[activityId];
  state.file = null;
  state.preview = null;
  state.uploadProgress = 0;
};

// Upload locally and create progress record
const uploadActivity = async (activity) => {
  const activityId = activity.id;
  const state = uploadState.value[activityId];
  
  if (!state.file) {
    swalError('Please select a file to upload');
    return;
  }
  
  state.isUploading = true;
  state.uploadProgress = 0;
  
  try {
    const formData = new FormData();
    formData.append('intervention_progress[intervention_id]', props.intervention.id);
    formData.append('intervention_progress[child_id]', props.child.id);
    formData.append('intervention_progress[intervention_activity_id]', activity.id);
    formData.append('intervention_progress[media]', state.file);
    
    // Perform upload with extended timeout for large files.
    const response = await create_intervention_progress(formData, {
        timeout: 1000 * 120,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            state.uploadProgress = percentCompleted;
        }
    });
    
    if (response.error) {
      swalError(response.result, true);
      return;
    }
    
    // Update activity status
    const ind = props.activities.findIndex(item => item.id == activity.id);
    props.activities[ind] = {
      ...props.activities[ind], 
      isCompleted: true,
      date_completed: response.result.data.attributes?.updated_at
    };
    
    // Reset upload state
    state.showUpload = false;
    state.file = null;
    state.preview = null;
    state.uploadProgress = 0;
    
    // Update points in header
    store.dispatch('rewards/fetchTotalPoints');
    
    swalSuccess('Activity completed successfully! 🎉');
    
  } catch (error) {
    // Provide a descriptive error message depending on the failure type.
    let errorMsg = 'Failed to upload. Please try again.';
    if (error.message?.includes('timeout')) {
      errorMsg = 'Upload timed out. The file may be too large or the connection is slow. Please try again.';
    } else if (error.message?.includes('Network Error')) {
      errorMsg = 'Network error. Please check your internet connection and try again.';
    } else if (error.result?.error) {
      errorMsg = error.result.error;
    }
    swalError(errorMsg);
  } finally {
    state.isUploading = false;
  }
};

// Get file size in human readable format
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};


// View/download proof
const viewProof = async (activity) => {
  if (!activity.intervention_progress_id) {
    swalError('No proof uploaded for this activity');
    return;
  }
  
  proofMedia.value.isLoading = true;
  showProofModal.value = true;
  
  try {
    const response = await get_media_url(activity.intervention_progress_id);
    
    if (response.error) {
      swalError('Failed to load proof media');
      showProofModal.value = false;
      return;
    }
    
    proofMedia.value = {
      url: response.result.url,
      filename: response.result.filename,
      contentType: response.result.content_type,
      isLoading: false
    };
  } catch (error) {
    console.error('Error loading proof:', error);
    swalError('Failed to load proof media');
    showProofModal.value = false;
    proofMedia.value.isLoading = false;
  }
};

const closeProofModal = () => {
  showProofModal.value = false;
  proofMedia.value = {
    url: null,
    filename: null,
    contentType: null,
    isLoading: false
  };
};

</script>

<template>
  <div class="card">
    <card_header :is_narrow="true" title="Activities"
      extra_class="justify-content-between"
    >
      <router-link class="ms-2 btn btn-light-warning" 
        :to="{
          name: 'feedback',
          query: {
            trait: trait_config.attributes.name,
            child: child.attributes.name,
            id: child.id,
            age_group: age_group_term.name
          }
        }"
      >
        <vue-feather type="message-circle" size="1rem" class="me-1"></vue-feather>
        Send Feedback
      </router-link>
    </card_header>
    
    <div class="card-body">
      <template v-if="activities.length != 0">
        <template v-for="(activity, i) in activities" :key="activity.id">
          <div v-auto-animate class="activity-item mb-3">
            <!-- Activity Header -->
            <div class="activity-header">
              <div class="activity-info">
                <div class="activity-title">
                  <vue-feather 
                    :type="activity.isCompleted ? 'check-circle' : 'circle'" 
                    :class="activity.isCompleted ? 'text-success' : 'text-muted'"
                    size="1.2rem"
                    class="me-2"
                  ></vue-feather>
                  <h6 class="mb-0">{{ activity.title }}</h6>
                </div>
                
                <div v-if="activity.isCompleted" class="activity-status">
                  <span class="badge badge-light-success">
                    <vue-feather type="check" size="0.9rem"></vue-feather>
                    Completed
                  </span>
                  <small class="text-muted ms-2">
                    {{ formattedDate(activity.date_completed, {short: true}) }}
                  </small>
                </div>
              </div>
              
              <div class="activity-actions">
                <button 
                  v-if="!activity.isCompleted"
                  @click="toggleUpload(activity.id)" 
                  :class="uploadState[activity.id]?.showUpload ? 'btn-light-danger' : 'btn-light-primary'"
                  class="btn btn-sm"
                >
                  <vue-feather 
                    :type="uploadState[activity.id]?.showUpload ? 'x' : 'upload'" 
                    size="1rem"
                    class="me-1"
                  ></vue-feather>
                  {{ uploadState[activity.id]?.showUpload ? 'Cancel' : 'Upload Proof' }}
                </button>
                
                <button 
                  v-else
                  @click="viewProof(activity)"
                  class="btn btn-sm btn-light-success"
                >
                  <vue-feather type="image" size="1rem" class="me-1"></vue-feather>
                  View Proof
                </button>
              </div>
            </div>
            
            <!-- Upload Panel -->
            <div v-if="uploadState[activity.id]?.showUpload" class="upload-panel mt-3">
              <div 
                class="upload-dropzone"
                :class="{ 
                  'dragging': uploadState[activity.id]?.isDragging,
                  'has-file': uploadState[activity.id]?.file
                }"
                @dragover="handleDragOver($event, activity.id)"
                @dragleave="handleDragLeave($event, activity.id)"
                @drop="handleDrop($event, activity.id)"
              >
                <!-- No file selected -->
                <div v-if="!uploadState[activity.id]?.file" class="dropzone-content">
                  <vue-feather type="upload-cloud" size="3rem" class="text-primary mb-3"></vue-feather>
                  <h6>Drag & drop your file here</h6>
                  <p class="text-muted mb-3">or</p>
                  <label :for="'file-input-' + activity.id" class="btn btn-primary">
                    <vue-feather type="folder" size="1rem" class="me-1"></vue-feather>
                    Browse Files
                  </label>
                  <input 
                    :id="'file-input-' + activity.id"
                    type="file" 
                    class="d-none"
                    @change="handleFileSelect($event, activity.id)"
                    accept="image/*,video/*"
                  >
                  <p class="text-muted mt-3 small">
                    Supported: Images (JPEG, PNG, GIF, WebP) or Videos (MP4, MOV, AVI, WebM)<br>
                    Max size: 50MB
                  </p>
                </div>
                
                <!-- File preview -->
                <div v-else class="file-preview">
                  <div class="preview-container">
                    <img 
                      v-if="uploadState[activity.id]?.fileType === 'image'" 
                      :src="uploadState[activity.id]?.preview" 
                      alt="Preview"
                      class="preview-image"
                    >
                    <video 
                      v-else-if="uploadState[activity.id]?.fileType === 'video'"
                      :src="uploadState[activity.id]?.preview"
                      controls
                      class="preview-video"
                    ></video>
                  </div>
                  
                  <div class="file-info">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <vue-feather 
                          :type="uploadState[activity.id]?.fileType === 'image' ? 'image' : 'video'" 
                          size="1rem"
                          class="me-2"
                        ></vue-feather>
                        <strong>{{ uploadState[activity.id]?.file?.name }}</strong>
                      </div>
                      <button 
                        @click="removeFile(activity.id)"
                        class="btn btn-sm btn-light-danger"
                        :disabled="uploadState[activity.id]?.isUploading"
                      >
                        <vue-feather type="trash-2" size="0.9rem"></vue-feather>
                      </button>
                    </div>
                    <p class="text-muted small mb-0">
                      Size: {{ formatFileSize(uploadState[activity.id]?.file?.size) }}
                    </p>
                  </div>
                  
                  <!-- Upload progress -->
                  <div v-if="uploadState[activity.id]?.isUploading" class="upload-progress mt-3">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="small">Uploading...</span>
                      <span class="small">{{ uploadState[activity.id]?.uploadProgress }}%</span>
                    </div>
                    <div class="progress">
                      <div 
                        class="progress-bar progress-bar-striped progress-bar-animated" 
                        role="progressbar" 
                        :style="{ width: uploadState[activity.id]?.uploadProgress + '%' }"
                      ></div>
                    </div>
                  </div>
                  
                  <!-- Upload button -->
                  <button 
                    v-if="!uploadState[activity.id]?.isUploading"
                    @click="uploadActivity(activity)"
                    class="btn btn-success w-100 mt-3"
                  >
                    <vue-feather type="check" size="1rem" class="me-1"></vue-feather>
                    Submit Activity Proof
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
      
      <template v-else>
        <emptyCard :link="{
          routerObj: {name: 'dashboard'},
          type: 'link',
          desc: 'Go to dashboard',
          text: 'No activity yet..',
          class: 'w-50'
        }"/>
      </template>
    </div>
    
    <disabledCard :child_id="child.id"/>
    
    <!-- Proof Modal -->
    <div v-if="showProofModal" class="proof-modal" @click.self="closeProofModal">
      <div class="proof-modal-content">
        <div class="proof-modal-header">
          <h5 class="mb-0">Activity Proof</h5>
          <button @click="closeProofModal" class="btn-close"></button>
        </div>
        
        <div class="proof-modal-body">
          <div v-if="proofMedia.isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading proof...</p>
          </div>
          
          <div v-else-if="proofMedia.url" class="proof-content">
            <!-- Image -->
            <img 
              v-if="proofMedia.contentType?.startsWith('image/')"
              :src="proofMedia.url"
              :alt="proofMedia.filename"
              class="proof-media-image"
            >
            
            <!-- Video -->
            <video 
              v-else-if="proofMedia.contentType?.startsWith('video/')"
              :src="proofMedia.url"
              controls
              class="proof-media-video"
            ></video>
            
            <div class="proof-info mt-3">
              <p class="mb-2">
                <vue-feather type="file" size="1rem" class="me-2"></vue-feather>
                <strong>{{ proofMedia.filename }}</strong>
              </p>
              <a 
                :href="proofMedia.url" 
                download 
                class="btn btn-primary"
                target="_blank"
              >
                <vue-feather type="download" size="1rem" class="me-1"></vue-feather>
                Download
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.activity-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  transition: all 0.3s ease;
}

.activity-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.activity-info {
  flex: 1;
}

.activity-title {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.activity-status {
  display: flex;
  align-items: center;
}

.activity-actions {
  display: flex;
  gap: 0.5rem;
}

.upload-panel {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.upload-dropzone {
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.upload-dropzone.dragging {
  border-color: #4299e1;
  background: #ebf8ff;
}

.upload-dropzone.has-file {
  border-color: #48bb78;
  background: #f0fff4;
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.file-preview {
  text-align: left;
}

.preview-container {
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  max-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
}

.preview-image,
.preview-video {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
}

.file-info {
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.upload-progress .progress {
  height: 8px;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .activity-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .activity-actions {
    width: 100%;
  }
  
  .activity-actions button {
    flex: 1;
  }
}

/* Proof Modal Styles */
.proof-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.proof-modal-content {
  background: white;
  border-radius: 12px;
  max-width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.proof-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.proof-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.proof-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.proof-media-image,
.proof-media-video {
  max-width: 100%;
  max-height: 60vh;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.proof-info {
  width: 100%;
  text-align: center;
}
</style>